import type { Dashboard, SidebarFolder } from "@cd/core";

/** A sidebar section in the viewer's order: ones they haven't placed yet on top, newest first, then the placed ones. */
export function sidebarOrder<T extends Pick<Dashboard, "sidebarPosition" | "createdAt">>(ds: T[]): T[] {
  return [...ds].sort((a, b) => {
    const pa = a.sidebarPosition, pb = b.sidebarPosition;
    if (pa === undefined || pb === undefined) return pa === pb ? b.createdAt.localeCompare(a.createdAt) : pa === undefined ? -1 : 1;
    return pa - pb;
  });
}

/** The two sections outside any folder. Every other list key is a folder id. */
export const MINE = "mine";
export const SHARED = "shared";

type Listed = Pick<Dashboard, "id" | "sidebarPosition" | "sidebarFolderId" | "createdAt" | "sharedBy" | "shareStatus">;

/** Which dashboard ids are in each of the viewer's folders and sections, each in order. */
export interface SidebarLayout {
  folders: string[];
  lists: Record<string, string[]>;
}

/**
 * Lays out the sidebar: the viewer's own dashboards and accepted shares, each in its folder, or in
 * Mine or Shared with me when it's in none (or in one since deleted). Pending and dismissed shares
 * aren't listed, even if they were filed in a folder before.
 */
export function sidebarLayout(ds: Listed[], folders: Pick<SidebarFolder, "id" | "position">[]): SidebarLayout {
  const folderIds = [...folders].sort((a, b) => a.position - b.position).map((f) => f.id);
  const grouped: Record<string, Listed[]> = { [MINE]: [], [SHARED]: [], ...Object.fromEntries(folderIds.map((id) => [id, []])) };
  for (const d of ds) {
    if (d.sharedBy && d.shareStatus !== "accepted") continue;
    const folder = d.sidebarFolderId && grouped[d.sidebarFolderId] ? d.sidebarFolderId : undefined;
    grouped[folder ?? (d.sharedBy ? SHARED : MINE)]!.push(d);
  }
  return { folders: folderIds, lists: Object.fromEntries(Object.entries(grouped).map(([k, v]) => [k, sidebarOrder(v).map((d) => d.id)])) };
}

/** Folders take anything; Mine only the viewer's own, Shared with me only shares. */
export const canHold = (list: string, d: Pick<Dashboard, "sharedBy">) => (list === MINE ? !d.sharedBy : list === SHARED ? !!d.sharedBy : true);

/** The list a dashboard is in. */
export const listOf = (layout: SidebarLayout, id: string) => Object.keys(layout.lists).find((k) => layout.lists[k]!.includes(id));

/** `id` moved into `to` at `index` (the end when undefined), taken out of wherever it was. */
export function moveTo(layout: SidebarLayout, id: string, to: string, index?: number): SidebarLayout {
  const lists = Object.fromEntries(Object.entries(layout.lists).map(([k, v]) => [k, v.filter((x) => x !== id)]));
  const target = lists[to] ?? [];
  target.splice(index ?? target.length, 0, id);
  return { ...layout, lists: { ...lists, [to]: target } };
}
